import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { formatDateTime as fmtDT } from '../../utils/dateUtils';

const STATUS_COLORS = { submitted: '#3b82f6', processing: '#f59e0b', active: '#10b981', rejected: '#dc2626' };
const STATUS_LABELS = { submitted: 'Submitted', processing: 'Processing', active: 'Active', rejected: 'Rejected' };
const DOC_TYPE_LABELS = { service_agreement: 'Service Agreement', property_info: 'Property Info Sheet', other: 'Other' };
const DOC_TYPE_OPTIONS = [
  { value: 'service_agreement', label: 'Service Agreement' },
  { value: 'property_info', label: 'Property Info Sheet' },
  { value: 'other', label: 'Other' },
];

export default function NewAccountDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can('manage_tasks');
  const fileRef = useRef();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadAccount();
  }, [id]); // eslint-disable-line

  async function loadAccount() {
    setLoading(true);
    try {
      const data = await api.getAccount(id);
      setAccount(data);
    } catch (err) {
      setError(err.message || 'Failed to load account');
    }
    setLoading(false);
  }

  async function handleStatusChange(newStatus) {
    if (newStatus === 'rejected' && !showReject) {
      setShowReject(true);
      return;
    }
    setStatusLoading(true);
    try {
      const body = { status: newStatus };
      if (newStatus === 'rejected') body.rejected_reason = rejectReason;
      const updated = await api.updateAccountStatus(id, body);
      setAccount(prev => ({ ...prev, ...updated, docs: prev.docs }));
      setShowReject(false);
      setRejectReason('');
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
    setStatusLoading(false);
  }

  function addUploadFiles(newFiles) {
    const added = Array.from(newFiles).map(f => ({ file: f, docType: 'other' }));
    setUploadFiles(prev => [...prev, ...added]);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleUpload() {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const docTypes = [];
      uploadFiles.forEach(({ file, docType }) => {
        fd.append('files', file);
        docTypes.push(docType);
      });
      fd.append('doc_types', JSON.stringify(docTypes));
      await api.uploadAccountDocs(id, fd);
      setUploadFiles([]);
      loadAccount();
    } catch (err) {
      setError(err.message || 'Failed to upload');
    }
    setUploading(false);
  }

  if (loading) return <div className="admin-page"><div className="loading-spinner">Loading...</div></div>;
  if (error && !account) return (
    <div className="admin-page">
      <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>{error}</div>
    </div>
  );
  if (!account) return null;

  const validTransitions = {
    submitted: ['processing', 'rejected'],
    processing: ['active', 'rejected'],
    rejected: ['submitted'],
  };
  const nextStatuses = validTransitions[account.status] || [];

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/sales/accounts/list')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#64748b' }}>
            &larr;
          </button>
          <h1 style={{ margin: 0 }}>{account.property_name}</h1>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: `${STATUS_COLORS[account.status]}18`, color: STATUS_COLORS[account.status],
          }}>
            {STATUS_LABELS[account.status]}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => navigate(`/sales/property-info?account_id=${id}`)}>
            Property Info Sheet
          </button>
          <button className="btn btn-secondary" onClick={() => navigate(`/sales/service-agreement?account_id=${id}`)}>
            Service Agreement
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: Account Info */}
        <div>
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Property Details</h3>
            <InfoRow label="Address" value={[account.address, account.city, account.state, account.zip].filter(Boolean).join(', ') || '-'} />
            <InfoRow label="Contact" value={account.contact_name || '-'} />
            <InfoRow label="Phone" value={account.contact_phone || '-'} />
            <InfoRow label="Email" value={account.contact_email || '-'} />
            <InfoRow label="Notes" value={account.notes || '-'} />
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Submission Info</h3>
            <InfoRow label="Submitted By" value={account.submitted_by_name || '-'} />
            <InfoRow label="Submitted" value={formatDateTime(account.created_at)} />
            {account.assigned_to_name && <InfoRow label="Assigned To" value={account.assigned_to_name} />}
            {account.activated_at && <InfoRow label="Activated" value={formatDateTime(account.activated_at)} />}
            {account.rejected_reason && <InfoRow label="Rejection Reason" value={account.rejected_reason} />}
          </div>

          {/* Status Actions */}
          {canManage && nextStatuses.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginTop: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Actions</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {nextStatuses.map(s => {
                  if (s === 'rejected' && !showReject) {
                    return (
                      <button key={s} className="btn btn-secondary" onClick={() => setShowReject(true)}
                        disabled={statusLoading}
                        style={{ borderColor: '#dc2626', color: '#dc2626' }}>
                        Reject
                      </button>
                    );
                  }
                  if (s === 'rejected') return null;
                  const labels = { processing: 'Start Processing', active: 'Activate Account', submitted: 'Resubmit' };
                  const colors = { processing: '#f59e0b', active: '#10b981', submitted: '#3b82f6' };
                  return (
                    <button key={s} className="btn btn-primary" onClick={() => handleStatusChange(s)}
                      disabled={statusLoading}
                      style={{ background: colors[s] }}>
                      {statusLoading ? 'Updating...' : labels[s] || s}
                    </button>
                  );
                })}
              </div>

              {showReject && (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', display: 'block', marginBottom: 6 }}>Rejection Reason</label>
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #fecaca', fontSize: 13, minHeight: 60, resize: 'vertical' }}
                    placeholder="Reason for rejection..." />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn btn-primary" onClick={() => handleStatusChange('rejected')}
                      disabled={statusLoading} style={{ background: '#dc2626' }}>
                      {statusLoading ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setShowReject(false); setRejectReason(''); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Documents */}
        <div>
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
              Documents ({account.docs?.length || 0})
            </h3>

            {account.docs?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {account.docs.map(doc => (
                  <div key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc',
                  }}>
                    <span style={{ fontSize: 20 }}>
                      {doc.original_name?.endsWith('.pdf') ? '📄' : '🖼️'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {doc.original_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type} &middot; {doc.uploaded_by_name || 'Unknown'} &middot; {formatDateTime(doc.created_at)}
                      </div>
                    </div>
                    <a href={`/api/uploads/${doc.file_path?.replace(/^.*\/uploads\//, '')}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                      View
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
                No documents uploaded
              </div>
            )}

            {/* Upload more */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Upload Additional Documents</h4>
              <div
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e2e8f0'; addUploadFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: '2px dashed #e2e8f0', borderRadius: 8, padding: 20,
                  textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Drop files or click to upload</div>
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" style={{ display: 'none' }}
                  onChange={e => addUploadFiles(e.target.files)} />
              </div>

              {uploadFiles.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {uploadFiles.map((f, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13,
                    }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                      <select value={f.docType} onChange={e => {
                        setUploadFiles(prev => prev.map((x, i) => i === idx ? { ...x, docType: e.target.value } : x));
                      }} style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11 }}>
                        {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button type="button" onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14 }}>&times;</button>
                    </div>
                  ))}
                  <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}
                    style={{ marginTop: 10, fontSize: 13 }}>
                    {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} file(s)`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', marginBottom: 10 }}>
      <div style={{ width: 120, fontSize: 12, fontWeight: 600, color: '#64748b', flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#0f172a' }}>{value}</div>
    </div>
  );
}

function formatDateTime(d) {
  if (!d) return '-';
  return fmtDT(d, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
